import { useEffect, useState } from 'react'
import { fetchLendingPositions } from '../../api/lending'
import { useAuth } from '../../auth/AuthContext'
import { useShimmerOnUpdate } from '../../hooks/useShimmerOnUpdate'
import type { LendingPosition } from '../../types/lending'
import type { StageAllocation } from '../../types/strategy'
import TokenIcon from '../common/TokenIcon'
import JupiterBorrowPanel from './JupiterBorrowPanel'

function usd(v: string) {
  return '$' + parseFloat(v).toLocaleString('en-US', { maximumFractionDigits: 2 })
}

function pct(v: string) {
  return (parseFloat(v) * 100).toFixed(2) + '%'
}

const COLS = 'grid-cols-[1.5fr_1.2fr_1.5fr_1fr_1fr_1fr]'

/**
 * portfolioLtv — the backend-provided LTV string from the portfolio delta message.
 * When provided, the LTV display uses the authoritative backend value.
 * The color coding is omitted here; use alert severity from the backend for risk indication.
 */
export default function LendingPanel({ onPanelUsd, wsData, portfolioLtv, lendStage, borrowStage }: {
  onPanelUsd?: (key: 'lending', value: { lendingUsd: number; borrowUsd: number }) => void
  wsData?: LendingPosition[] | null
  portfolioLtv?: string | null
  lendStage?: StageAllocation | null
  borrowStage?: StageAllocation | null
} = {}) {
  const { portfolioId } = useAuth()
  const [positions, setPositions] = useState<LendingPosition[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showManage, setShowManage] = useState(false)
  const shimmerRef = useShimmerOnUpdate(wsData)

  // Initial data: use batch snapshot from parent when available, otherwise fall back to
  // individual REST fetch. wsData intentionally excluded from deps.
  useEffect(() => {
    if (!portfolioId) return
    if (wsData != null) { setLoading(false); return }
    setLoading(true)
    fetchLendingPositions(portfolioId)
      .then(data => { setPositions(data); setError(null) })
      .catch(e => setError(String(e)))
      .finally(() => setLoading(false))
  }, [portfolioId])  

  // Live update from WebSocket push
  useEffect(() => {
    if (wsData == null) return
    setPositions(wsData)
    setLoading(false)
  }, [wsData])

  useEffect(() => {
    if (positions) {
      const lendingUsd = positions.filter(p => p.positionType === 'LENDING').reduce((s, p) => s + parseFloat(p.usdValue), 0)
      const borrowUsd  = positions.filter(p => p.positionType === 'BORROW').reduce((s, p) => s + parseFloat(p.usdValue), 0)
      onPanelUsd?.('lending', { lendingUsd, borrowUsd })
    }
  }, [positions])

  const lending = positions?.filter(p => p.positionType === 'LENDING') ?? []
  const borrow  = positions?.filter(p => p.positionType === 'BORROW')  ?? []

  return (
    <div ref={shimmerRef} className="bg-surface-card rounded-xl border border-fringe border-spectral font-sans overflow-hidden flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-2 px-5 py-3 border-b border-fringe shrink-0">
        <div className="w-7 h-7 rounded-lg bg-accent-purple/10 flex items-center justify-center text-accent-purple">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-4 h-4">
            <rect x="3" y="3" width="18" height="4" rx="1" />
            <rect x="3" y="10" width="18" height="4" rx="1" />
            <rect x="3" y="17" width="18" height="4" rx="1" />
          </svg>
        </div>
        <span className="text-sm font-semibold text-ink">Lending &amp; Borrow</span>
        {positions && (
          <span className="ml-1 text-xs bg-accent-purple/20 text-accent-purple px-1.5 py-0.5 rounded-full font-medium">
            {positions.length}
          </span>
        )}
        <div className="ml-auto flex items-center gap-3">
          {positions && positions.length > 0 && (() => {
            const totalSupplied = lending.reduce((s, p) => s + parseFloat(p.usdValue), 0)
            const totalBorrowed = borrow.reduce((s, p) => s + parseFloat(p.usdValue), 0)
            const netValue = totalSupplied - totalBorrowed
            const displayLtv = portfolioLtv != null ? parseFloat(portfolioLtv) : null
            return (
              <>
                <span className="text-sm font-semibold text-ink">{usd(String(netValue))}</span>
                {displayLtv !== null && (
                  <span className="text-xs text-gray-500">
                    LTV <span className="font-semibold text-ink-soft">{(displayLtv * 100).toFixed(1)}%</span>
                  </span>
                )}
              </>
            )
          })()}
          <button
            onClick={() => setShowManage(true)}
            className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-purple-600/20 hover:bg-purple-600/40 text-indigo-400 hover:text-purple-300 text-[11px] font-medium transition-colors"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 20v-8m0 0V4m0 8h8m-8 0H4"/>
            </svg>
            Manage
          </button>
        </div>
      </div>

      {/* Body */}
      {error && (
        <p className="text-accent-red text-xs p-4">{error}</p>
      )}
      {loading && (
        <div className="animate-pulse p-4 space-y-2">
          <div className="h-3 w-full bg-surface rounded"/>
          <div className="h-3 w-3/4 bg-surface rounded"/>
          <div className="h-3 w-5/6 bg-surface rounded"/>
        </div>
      )}
      {!loading && positions && positions.length === 0 && (
        <div className="flex flex-col items-center gap-3 py-8">
          <p className="text-gray-500 text-sm">No lending positions found</p>
          <button
            onClick={() => setShowManage(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-purple-600/20 hover:bg-purple-600/40 text-indigo-400 hover:text-purple-300 text-xs font-medium transition-colors"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 20v-8m0 0V4m0 8h8m-8 0H4"/>
            </svg>
            Manage on Jupiter
          </button>
        </div>
      )}
      {!loading && positions && positions.length > 0 && (
        <div className="flex flex-col min-h-0 flex-1 overflow-hidden">
          <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-surface flex-1">
            <div className="min-w-[480px]">
          {/* Column headers */}
          <div className={`grid ${COLS} gap-x-3 px-4 sm:px-5 py-2 text-xs text-gray-500 uppercase tracking-wider border-b border-fringe`}>
            <span>Protocol</span>
            <span>Asset</span>
            <span className="text-right">Balance</span>
            <span className="text-right">Value</span>
            <span className="text-right">APY</span>
            <span className="text-right">Price USD</span>
          </div>

          <div className="overflow-y-auto scrollbar-thin scrollbar-thumb-surface scrollbar-track-transparent max-h-[320px] lg:max-h-none">
            {lending.length > 0 && (
              <>
                <div className="px-5 py-1.5 text-xs font-semibold text-accent-green/80 bg-accent-green/5 border-b border-fringe">
                  Lending ▸
                </div>
                <div className="divide-y divide-white/5">
                  {lending.map((p, i) => (
                    <PositionRow key={p.protocol + p.symbol + i} p={p} apyColor="text-accent-green" />
                  ))}
                </div>
              </>
            )}
            {borrow.length > 0 && (
              <>
                <div className="px-5 py-1.5 text-xs font-semibold text-accent-red/80 bg-accent-red/5 border-b border-fringe">
                  Borrowing ▸
                </div>
                <div className="divide-y divide-white/5">
                  {borrow.map((p, i) => (
                    <PositionRow key={p.protocol + p.symbol + i} p={p} apyColor="text-accent-red" />
                  ))}
                </div>
              </>
            )}
          </div>
            </div>
          </div>
        </div>
      )}
      {showManage && (
        <JupiterBorrowPanel
          positions={positions ?? []}
          onClose={() => setShowManage(false)}
          lendStage={lendStage ?? undefined}
          borrowStage={borrowStage ?? undefined}
        />
      )}
    </div>
  )
}

function PositionRow({ p, apyColor }: {
  p: LendingPosition
  apyColor: string
}) {
  const amount = parseFloat(p.amount)
  const usdVal = parseFloat(p.usdValue)
  const priceUsd = amount > 0 ? usdVal / amount : null
  return (
    <div className={`grid ${COLS} gap-x-3 px-4 sm:px-5 py-2.5 sm:py-3 items-center hover:bg-surface-hover/30 transition-colors text-sm`}>
      <span className="font-medium text-ink capitalize">{p.protocol}</span>
      <span className="text-ink-soft flex items-center gap-1.5">
        <TokenIcon symbol={p.symbol} mint={p.mintAddress} size={18} />
        {p.symbol}
      </span>
      <span className="text-right text-ink-soft">{amount.toFixed(4)}</span>
      <span className="text-right text-ink font-semibold">{usd(p.usdValue)}</span>
      <span className={`text-right font-medium ${apyColor}`}>{pct(p.apy)}</span>
      <span className="text-right text-accent-blue">
        {priceUsd !== null ? usd(String(priceUsd)) : '—'}
      </span>
    </div>
  )
}
